import StatusBadge from '../StatusBadge';
import WorkflowStepper from '../WorkflowStepper';
import { StatusTimeline } from '../StatusTimeline';
import { dueInfo } from '../../utils/dueDate';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Printer } from 'lucide-react';
import { formatRupiah } from '../../utils/printHelper';
import { InvoiceAmountSummary } from './InvoiceAmountSummary';

export function InvoiceDetailDialog({ open, onOpenChange, invoice, onPrint }) {
  if (!invoice) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Detail Invoice</span>
            {onPrint && (
              <Button size="sm" variant="outline" onClick={() => onPrint(invoice)}>
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4" id="print-content">
          <WorkflowStepper currentStatus={invoice.status} />
          <div className="grid grid-cols-2 gap-4">
            <div><p className="text-sm text-slate-600">Invoice Number</p><p className="font-mono font-semibold">{invoice.invoice_number}</p></div>
            <div><p className="text-sm text-slate-600">Tanggal Invoice</p><p className="font-semibold">{new Date(invoice.invoice_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div>
            <div><p className="text-sm text-slate-600">Supplier</p><p className="font-semibold">{invoice.supplier_name}</p></div>
            <div><p className="text-sm text-slate-600">Email Supplier</p><p className="font-semibold">{invoice.email_supplier || invoice.supplier_data?.email || '-'}</p></div>
            <div><p className="text-sm text-slate-600">Ditujukan untuk</p><p className="font-semibold">{invoice.company_name || '-'}</p></div>
            <div><p className="text-sm text-slate-600">Termin Pembayaran</p><p className="font-semibold">{invoice.termin_pembayaran ? `${invoice.termin_pembayaran} Hari` : '-'}</p></div>
            <div><p className="text-sm text-slate-600">Jenis Pengiriman</p><p className="font-semibold capitalize">{invoice.jenis_pengiriman === 'full' ? 'Full (Sekaligus)' : 'Partial (Bertahap)'}</p></div>
          </div>

          {invoice.alamat_pengiriman?.length > 0 && (
            <div>
              <p className="text-sm text-slate-600 mb-1">Alamat Pengiriman</p>
              {invoice.alamat_pengiriman.map((addr, idx) => (
                <p key={idx} className="text-sm bg-slate-50 p-2 rounded mb-1">{addr}</p>
              ))}
            </div>
          )}

          <div>
            <p className="text-sm text-slate-600 mb-2">Items</p>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              {invoice.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center border-b pb-2 last:border-b-0">
                  <div>
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-sm text-slate-600">
                      {item.quantity} x Rp {formatRupiah(item.unit_price || 0)}
                      {item.delivery_date && ` | Kirim: ${new Date(item.delivery_date).toLocaleDateString('id-ID')}`}
                    </p>
                  </div>
                  <p className="font-mono font-semibold">Rp {formatRupiah((item.quantity || 0) * (item.unit_price || 0))}</p>
                </div>
              ))}
            </div>
          </div>

          <InvoiceAmountSummary invoice={invoice} className="bg-orange-50 border-orange-200" />

          {invoice.notes && (
            <div><p className="text-sm text-slate-600">Catatan</p><p className="text-sm bg-slate-50 p-2 rounded">{invoice.notes}</p></div>
          )}
          {(invoice.status === 'returned' || invoice.return_count > 0) && invoice.return_reason && (
            <div className="bg-orange-50 border border-orange-300 p-3 rounded-lg" data-testid="invoice-detail-return-info">
              <p className="text-sm font-semibold text-orange-800">{invoice.status === 'returned' ? 'Dikembalikan AP ke Clerk' : `Pernah dikembalikan (${invoice.return_count}x)`}</p>
              <p className="text-sm text-orange-900">Alasan: {invoice.return_reason}</p>
              {invoice.returned_at && <p className="text-xs text-orange-700">{new Date(invoice.returned_at).toLocaleString('id-ID')} · {invoice.returned_by_name}</p>}
              {invoice.resubmitted_at && invoice.status !== 'returned' && <p className="text-xs text-orange-700">Disubmit ulang {new Date(invoice.resubmitted_at).toLocaleString('id-ID')}</p>}
            </div>
          )}

          {invoice.uses_va && (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg" data-testid="invoice-detail-va-info">
              <p className="text-sm font-semibold text-amber-800 mb-1">Virtual Account</p>
              <div className="text-sm space-y-1">
                <p><span className="text-slate-500">Nomor VA:</span> <span className="font-mono font-bold text-amber-700">{invoice.va_nomor || '-'}</span></p>
                <p><span className="text-slate-500">Nama VA:</span> <span className="font-bold text-amber-700">{invoice.va_nama || '-'}</span></p>
              </div>
            </div>
          )}

          <div className="flex justify-between text-sm text-slate-600 pt-2 border-t">
            <span>Dibuat oleh: {invoice.created_by_name}</span>
            <span>Status: <StatusBadge status={invoice.status} /></span>
          </div>
          {dueInfo(invoice) && (
            <p className="text-sm text-slate-600" data-testid="invoice-detail-due">
              Jatuh tempo: <span className="font-mono font-semibold">{dueInfo(invoice).dateText}</span>
              <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${dueInfo(invoice).color}`}>{dueInfo(invoice).label}</span>
              <span className="text-xs text-slate-400 ml-2">(tanggal invoice + termin {invoice.termin_pembayaran || 7} hari)</span>
            </p>
          )}
          <StatusTimeline
            docType="invoice_full"
            docId={invoice.id}
            fallbackDates={[
              { label: 'Dibuat / submit dari PO', at: invoice.submitted_from_po_at || invoice.created_at, by: invoice.submitted_from_po_by_name || invoice.created_by_name },
              { label: 'Dokumen dikirim', at: invoice.dokumen_dikirim_at, by: invoice.dokumen_dikirim_by_name },
              { label: 'Dokumen diterima AP', at: invoice.dokumen_diterima_at, by: invoice.dokumen_diterima_by_name },
              { label: 'Siap Proses (Proses Bayar)', at: invoice.submitted_to_payment_at, by: invoice.submitted_to_payment_by_name },
              { label: 'Terbayar', at: invoice.paid_at, by: invoice.paid_by_name },
            ]}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}


